import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageSquare, Mail, FolderSync, FileText, Calendar, CheckCircle2, XCircle, Loader2, Send, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const STATUS_BADGE = {
  active: { label: "Active", cls: "bg-[#F0FDF4] border-[#10B981] text-[#10B981]" },
  configured: { label: "Configured", cls: "bg-[#FEF3C7] border-[#F59E0B] text-[#92400E]" },
  not_configured: { label: "Not Configured", cls: "bg-[#F8FAFC] border-[#E2E8F0] text-[#94A3B8]" },
  connected: { label: "Connected", cls: "bg-[#F0FDF4] border-[#10B981] text-[#10B981]" },
  error: { label: "Error", cls: "bg-[#FEF2F2] border-[#DC2626] text-[#DC2626]" },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.not_configured;
  return <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider border ${s.cls}`} data-testid="status-badge">{s.label}</span>;
}

function Toggle({ value, onChange, testId }) {
  return (
    <button onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition-colors ${value ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid={testId}>
      <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${value ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function M365HubPage({ onLogout, userRole, isFeatureEnabled }) {
  const [tab, setTab] = useState("teams");
  const [m365Status, setM365Status] = useState(null);
  const [teamsConfig, setTeamsConfig] = useState(null);
  const [outlookConfig, setOutlookConfig] = useState(null);
  const [spConfig, setSpConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [statusResp, teamsResp, outlookResp, spResp] = await Promise.all([
        axios.get(`${API}/admin/m365-status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/teams-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/outlook-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/sharepoint-config`, { headers: getAuthHeaders() }),
      ]);
      setM365Status(statusResp.data);
      setTeamsConfig(teamsResp.data);
      setOutlookConfig(outlookResp.data);
      setSpConfig(spResp.data);
    } catch { toast.error("Failed to load M365 configuration"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async (endpoint, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/${endpoint}`, data, { headers: getAuthHeaders() });
      toast.success("Configuration saved");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleTest = async (endpoint) => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await axios.post(`${API}/${endpoint}`, {}, { headers: getAuthHeaders() });
      setTestResult(resp.data);
      if (resp.data.status === "success" || resp.data.status === "connected") toast.success(resp.data.message);
      else toast.warning(resp.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || "Connection test failed";
      setTestResult({ status: "error", message: msg });
      toast.error(msg);
    }
    finally { setTesting(false); }
  };

  const updateTeams = (k, v) => setTeamsConfig(p => ({ ...p, [k]: v }));
  const updateOutlook = (k, v) => setOutlookConfig(p => ({ ...p, [k]: v }));
  const updateSP = (k, v) => setSpConfig(p => ({ ...p, [k]: v }));

  if (loading) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#64748B]" size={24} /></div></Layout>;
  }

  const TABS = [
    { id: "teams", label: "Teams", icon: MessageSquare, status: m365Status?.teams?.status },
    { id: "outlook_email", label: "Outlook Email", icon: Mail, status: m365Status?.outlook_email?.status },
    { id: "calendar", label: "Calendar Feed", icon: Calendar, status: m365Status?.outlook_calendar?.status },
    { id: "sharepoint", label: "SharePoint", icon: FolderSync, status: m365Status?.sharepoint?.status },
    { id: "office", label: "Office Preview", icon: FileText, status: m365Status?.office_preview?.status },
  ];

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="m365-hub-page">
        <PageHeader title="Microsoft 365 Hub" subtitle="Manage all M365 integrations — Teams, Outlook, SharePoint, Office" testId="m365-hub-title" />

        {/* Status Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="m365-status-grid">
          {TABS.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setTestResult(null); }}
              className={`p-4 border text-left transition-colors ${tab === t.id ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E2E8F0] bg-white hover:border-[#94A3B8]"}`}
              data-testid={`m365-tab-${t.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <t.icon size={16} className={tab === t.id ? "text-[#2563EB]" : "text-[#64748B]"} />
                <span className="text-sm font-bold text-[#0F172A]">{t.label}</span>
              </div>
              <StatusBadge status={t.status} />
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-[#E2E8F0]">
          {/* Teams */}
          {tab === "teams" && teamsConfig && (
            <div className="p-6 space-y-5" data-testid="teams-panel">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><MessageSquare size={20} className="text-[#6366F1]" /> Microsoft Teams Webhook</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTest("admin/teams-test")} disabled={testing || !teamsConfig.webhook_url} className="rounded-none" data-testid="teams-test-btn">
                    {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Test
                  </Button>
                  <Button size="sm" onClick={() => handleSave("admin/teams-config", teamsConfig)} disabled={saving} className="rounded-none bg-[#2563EB] text-white" data-testid="teams-save-btn">
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium w-20">Enabled</Label>
                <Toggle value={teamsConfig.enabled} onChange={v => updateTeams("enabled", v)} testId="teams-enabled" />
                <span className="text-xs text-[#64748B]">{teamsConfig.enabled ? "Active — notifications will fire" : "Disabled"}</span>
              </div>
              <div>
                <Label className="text-sm font-medium">Incoming Webhook URL</Label>
                <Input value={teamsConfig.webhook_url || ""} onChange={e => updateTeams("webhook_url", e.target.value)} placeholder="https://outlook.office.com/webhook/..." className="mt-1 rounded-none font-mono text-xs" data-testid="teams-webhook-url" />
                <p className="text-xs text-[#94A3B8] mt-1">Create an Incoming Webhook connector in your Teams channel &gt; Connectors</p>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Notification Triggers</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { key: "notify_critical_alerts", label: "Critical Alerts", desc: "Missing blueprints, flagged docs" },
                    { key: "notify_overdue_maintenance", label: "Overdue Maintenance", desc: "Past scheduled date" },
                    { key: "notify_scan_failures", label: "Scan Failures", desc: "Network scan errors" },
                    { key: "notify_work_orders", label: "Work Orders", desc: "Create, complete, approve" },
                  ].map(t => (
                    <div key={t.key} className="flex items-center justify-between p-3 border border-[#E2E8F0]" data-testid={`teams-trigger-${t.key}`}>
                      <div><p className="text-sm font-medium text-[#0F172A]">{t.label}</p><p className="text-[10px] text-[#94A3B8]">{t.desc}</p></div>
                      <Toggle value={teamsConfig[t.key]} onChange={v => updateTeams(t.key, v)} testId={`teams-toggle-${t.key}`} />
                    </div>
                  ))}
                </div>
              </div>
              {testResult && (
                <div className={`p-3 border ${testResult.status === "success" ? "border-[#10B981] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`} data-testid="teams-test-result">
                  <div className="flex items-center gap-2">
                    {testResult.status === "success" ? <CheckCircle2 size={16} className="text-[#10B981]" /> : <XCircle size={16} className="text-[#DC2626]" />}
                    <span className="text-sm font-medium">{testResult.message}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Outlook Email */}
          {tab === "outlook_email" && outlookConfig && (
            <div className="p-6 space-y-5" data-testid="outlook-email-panel">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><Mail size={20} className="text-[#0EA5E9]" /> Outlook Email Notifications</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTest("admin/outlook-test")} disabled={testing || !outlookConfig.sender_email} className="rounded-none" data-testid="outlook-test-btn">
                    {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Test SMTP
                  </Button>
                  <Button size="sm" onClick={() => handleSave("admin/outlook-config", outlookConfig)} disabled={saving} className="rounded-none bg-[#2563EB] text-white" data-testid="outlook-save-btn">
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium w-20">Enabled</Label>
                <Toggle value={outlookConfig.enabled} onChange={v => updateOutlook("enabled", v)} testId="outlook-enabled" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">SMTP Server</Label>
                  <Input value={outlookConfig.smtp_server || ""} onChange={e => updateOutlook("smtp_server", e.target.value)} className="mt-1 rounded-none font-mono text-xs" data-testid="outlook-smtp-server" />
                </div>
                <div>
                  <Label className="text-sm font-medium">SMTP Port</Label>
                  <Input type="number" value={outlookConfig.smtp_port || 587} onChange={e => updateOutlook("smtp_port", parseInt(e.target.value) || 587)} className="mt-1 rounded-none font-mono text-xs" data-testid="outlook-smtp-port" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Sender Email</Label>
                  <Input value={outlookConfig.sender_email || ""} onChange={e => updateOutlook("sender_email", e.target.value)} placeholder="vault@company.com" className="mt-1 rounded-none font-mono text-xs" data-testid="outlook-sender-email" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Sender Password / App Password</Label>
                  <Input type="password" value={outlookConfig.sender_password || ""} onChange={e => updateOutlook("sender_password", e.target.value)} className="mt-1 rounded-none font-mono text-xs" data-testid="outlook-sender-password" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Recipients (comma-separated)</Label>
                <Input value={(outlookConfig.recipients || []).join(", ")} onChange={e => updateOutlook("recipients", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="admin@company.com, super@company.com" className="mt-1 rounded-none text-xs" data-testid="outlook-recipients" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Email Triggers</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    { key: "notify_overdue", label: "Overdue Maintenance" },
                    { key: "notify_work_orders", label: "Work Order Updates" },
                    { key: "notify_scan_complete", label: "Scan Completions" },
                  ].map(t => (
                    <div key={t.key} className="flex items-center justify-between p-3 border border-[#E2E8F0]" data-testid={`outlook-trigger-${t.key}`}>
                      <span className="text-sm font-medium text-[#0F172A]">{t.label}</span>
                      <Toggle value={outlookConfig[t.key]} onChange={v => updateOutlook(t.key, v)} testId={`outlook-toggle-${t.key}`} />
                    </div>
                  ))}
                </div>
              </div>
              {testResult && (
                <div className={`p-3 border ${testResult.status === "connected" ? "border-[#10B981] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`} data-testid="outlook-test-result">
                  <div className="flex items-center gap-2">
                    {testResult.status === "connected" ? <CheckCircle2 size={16} className="text-[#10B981]" /> : <XCircle size={16} className="text-[#DC2626]" />}
                    <span className="text-sm font-medium">{testResult.message}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Calendar Feed */}
          {tab === "calendar" && (
            <div className="p-6 space-y-5" data-testid="calendar-panel">
              <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><Calendar size={20} className="text-[#D97706]" /> Outlook Calendar Integration</h2>
              <p className="text-sm text-[#64748B]">Vault provides an iCalendar (.ics) feed for maintenance schedules. Subscribe in Outlook, Google Calendar, or any calendar app for real-time maintenance visibility.</p>
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 space-y-3">
                <div>
                  <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">All Maintenance Feed</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly value={`${window.location.origin}/api/maintenance/calendar.ics`} className="rounded-none font-mono text-xs flex-1" data-testid="calendar-all-url" />
                    <Button variant="outline" size="sm" className="rounded-none" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/maintenance/calendar.ics`); toast.success("URL copied"); }} data-testid="calendar-copy-all">
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">My Assigned Maintenance</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly value={`${window.location.origin}/api/maintenance/calendar.ics?assigned_to=me`} className="rounded-none font-mono text-xs flex-1" data-testid="calendar-my-url" />
                    <Button variant="outline" size="sm" className="rounded-none" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/maintenance/calendar.ics?assigned_to=me`); toast.success("URL copied"); }} data-testid="calendar-copy-my">
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-[#F0F9FF] border border-[#BAE6FD] p-4">
                <h3 className="font-bold text-sm text-[#0369A1] mb-2">How to Subscribe in Outlook</h3>
                <ol className="text-xs text-[#0C4A6E] space-y-1 list-decimal ml-4">
                  <li>Open Outlook &gt; Calendar &gt; Add Calendar &gt; From Internet</li>
                  <li>Paste the feed URL above</li>
                  <li>Name it "Vault Maintenance"</li>
                  <li>Click Import — calendar will auto-refresh</li>
                </ol>
              </div>
              <StatusBadge status="active" />
              <span className="text-xs text-[#64748B] ml-2">Always available — no configuration needed</span>
            </div>
          )}

          {/* SharePoint */}
          {tab === "sharepoint" && spConfig && (
            <div className="p-6 space-y-5" data-testid="sharepoint-panel">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><FolderSync size={20} className="text-[#0EA5E9]" /> SharePoint Blueprint Sync</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTest("admin/sharepoint-test")} disabled={testing || !spConfig.tenant_id} className="rounded-none" data-testid="sp-test-btn">
                    {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Test
                  </Button>
                  <Button size="sm" onClick={() => handleSave("admin/sharepoint-config", spConfig)} disabled={saving} className="rounded-none bg-[#2563EB] text-white" data-testid="sp-save-btn">
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium w-28">Sync Enabled</Label>
                <Toggle value={spConfig.sync_enabled} onChange={v => updateSP("sync_enabled", v)} testId="sp-sync-enabled" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Tenant ID</Label>
                  <Input value={spConfig.tenant_id || ""} onChange={e => updateSP("tenant_id", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="mt-1 rounded-none font-mono text-xs" data-testid="sp-tenant-id" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Client ID (App Registration)</Label>
                  <Input value={spConfig.client_id || ""} onChange={e => updateSP("client_id", e.target.value)} className="mt-1 rounded-none font-mono text-xs" data-testid="sp-client-id" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Client Secret</Label>
                  <Input type="password" value={spConfig.client_secret || ""} onChange={e => updateSP("client_secret", e.target.value)} className="mt-1 rounded-none font-mono text-xs" data-testid="sp-client-secret" />
                </div>
                <div>
                  <Label className="text-sm font-medium">SharePoint Site URL</Label>
                  <Input value={spConfig.site_url || ""} onChange={e => updateSP("site_url", e.target.value)} placeholder="https://company.sharepoint.com/sites/engineering" className="mt-1 rounded-none font-mono text-xs" data-testid="sp-site-url" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Document Library</Label>
                  <Input value={spConfig.library_name || ""} onChange={e => updateSP("library_name", e.target.value)} className="mt-1 rounded-none text-xs" data-testid="sp-library" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Sync Direction</Label>
                  <select value={spConfig.sync_direction || "both"} onChange={e => updateSP("sync_direction", e.target.value)} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" data-testid="sp-sync-direction">
                    <option value="pull">Pull from SharePoint</option>
                    <option value="push">Push to SharePoint</option>
                    <option value="both">Bi-directional</option>
                  </select>
                </div>
              </div>
              {testResult && (
                <div className={`p-3 border ${testResult.status === "connected" ? "border-[#10B981] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`} data-testid="sp-test-result">
                  <div className="flex items-center gap-2">
                    {testResult.status === "connected" ? <CheckCircle2 size={16} className="text-[#10B981]" /> : <XCircle size={16} className="text-[#DC2626]" />}
                    <span className="text-sm font-medium">{testResult.message}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Office Preview */}
          {tab === "office" && (
            <div className="p-6 space-y-5" data-testid="office-panel">
              <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><FileText size={20} className="text-[#10B981]" /> Office Document Preview</h2>
              <p className="text-sm text-[#64748B]">Vault renders Microsoft Office documents (Word, Excel, PowerPoint) directly in the browser without requiring Office installation.</p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { fmt: "DOCX", desc: "Word documents rendered to HTML", color: "#2563EB" },
                  { fmt: "XLSX", desc: "Excel spreadsheets with table rendering", color: "#10B981" },
                  { fmt: "PPTX", desc: "PowerPoint slides with basic rendering", color: "#D97706" },
                ].map(f => (
                  <div key={f.fmt} className="border border-[#E2E8F0] p-4 text-center">
                    <div className="text-2xl font-bold font-mono mb-1" style={{ color: f.color }}>.{f.fmt.toLowerCase()}</div>
                    <p className="text-xs text-[#64748B]">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="bg-[#F0FDF4] border border-[#10B981] p-3 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#10B981]" />
                <span className="text-sm font-medium text-[#10B981]">Always active — no configuration needed</span>
              </div>
              <p className="text-xs text-[#94A3B8]">Documents are converted server-side using python-docx and openpyxl. For full-fidelity viewing, users can also open via Office Online if the document is hosted on SharePoint.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
